import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Music, Play } from 'lucide-react';
import { cn } from '@/lib/utils';

const MobileVisualizer = React.memo(function MobileVisualizer({
  analyserRef,
  dataArrayRef,
  isPlaying = false,
  visualizerType = 'bars',
  setVisualizerType,
  currentSong,
  currentTime = 0,
  orientation = 'portrait'
}) {
  const canvasRef = useRef(null);
  const animationIdRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }
    
    const dpr = window.devicePixelRatio || 1;

    // Set canvas size to match display size accounting for device pixel ratio
    const resizeCanvas = () => {
      try {
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
      } catch (error) {
        console.error('Error resizing canvas:', error);
      }
    };

    resizeCanvas();
    const handleOrientationChange = () => setTimeout(resizeCanvas, 100);
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('orientationchange', handleOrientationChange);

    const draw = () => {
      if (!ctx || !canvas) return;

      const width = canvas.width / dpr;
      const height = canvas.height / dpr;

      // Clear canvas with gradient background
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, 'rgba(15, 23, 42, 0.95)');
      gradient.addColorStop(0.5, 'rgba(30, 58, 138, 0.95)');
      gradient.addColorStop(1, 'rgba(49, 46, 129, 0.95)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      if (!analyserRef.current || !dataArrayRef.current || !isPlaying) {
        animationIdRef.current = requestAnimationFrame(draw);
        return;
      }

      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      const bufferLength = dataArrayRef.current.length;
      const data = dataArrayRef.current;

      // Bars visualizer - optimized for mobile with full color spectrum
      const barCount = orientation === 'portrait' ? 40 : 80;
      const gap = 1;
      // Only sample the lower 65% of FFT bins — that covers ~0–14kHz where all
      // musical content lives. Sampling the full buffer maps the rightmost bars
      // to >17kHz which is essentially silent, leaving the right edge empty.
      const maxBin = Math.floor(bufferLength * 0.65);
      const step = Math.floor(maxBin / barCount);

      for (let i = 0; i < barCount; i++) {
        const value = data[i * step] || 0;
        const percent = value / 255;
        // 2px minimum ensures every bar is always visible, filling the full width
        const barHeight = Math.max(2, percent * height * 0.8);

        // Proportional slot positions guarantee bars fill the full canvas width.
        // The last bar gets no trailing gap so it pins to the right edge exactly.
        const slotStart = Math.round((i / barCount) * width);
        const slotEnd = i < barCount - 1
          ? Math.round(((i + 1) / barCount) * width)
          : width;
        const isLastBar = i === barCount - 1;
        const currentBarWidth = Math.max(1, slotEnd - slotStart - (isLastBar ? 0 : gap));
        const x = slotStart;
        const y = height - barHeight;

        // Map position to full color spectrum (red -> orange -> yellow -> green -> cyan -> blue -> purple)
        const hue = (i / barCount) * 360; // 0-360 degrees for full spectrum
        const saturation = 70 + (percent * 30); // 70-100% saturation
        const lightness = 50 + (percent * 15); // 50-65% lightness
        const alpha = 0.7 + (percent * 0.3);

        // Create gradient for each bar with spectrum colors
        const barGradient = ctx.createLinearGradient(x, y, x, height);
        barGradient.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness + 10}%, ${alpha})`);
        barGradient.addColorStop(0.5, `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`);
        barGradient.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness - 10}%, ${alpha})`);

        ctx.fillStyle = barGradient;
        ctx.fillRect(x, y, currentBarWidth, barHeight);
        
        // Add glow effect for high values
        if (percent > 0.7) {
          ctx.shadowBlur = 10;
          ctx.shadowColor = `hsla(${hue}, ${saturation}%, ${lightness}%, 0.6)`;
          ctx.fillRect(x, y, currentBarWidth, barHeight);
          ctx.shadowBlur = 0;
        }
      }

      animationIdRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [analyserRef, dataArrayRef, isPlaying, visualizerType, orientation]);

  return (
    <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full touch-none"
      />
      
      {/* Show placeholder when no song is playing */}
      {!currentSong && (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center"
          >
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-600/20 flex items-center justify-center mb-6 backdrop-blur-sm border border-white/10">
              <Music className="h-16 w-16 text-white/30" />
            </div>
            <p className="text-white/70 text-center text-lg font-medium mb-2">
              No song playing
            </p>
            <p className="text-white/40 text-sm text-center max-w-xs">
              Tap the playlist icon at the top to add songs
            </p>
          </motion.div>
        </div>
      )}
      
      {/* Current song overlay (only show when playing) */}
      {currentSong && isPlaying && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute top-0 left-0 right-0 p-6 bg-gradient-to-b from-black/40 to-transparent pointer-events-none"
        >
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                boxShadow: [
                  '0 0 0 0 rgba(59, 130, 246, 0.7)',
                  '0 0 0 10px rgba(59, 130, 246, 0)',
                  '0 0 0 0 rgba(59, 130, 246, 0)'
                ]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0 shadow-lg"
            >
              <Play className="h-5 w-5 text-white" fill="currentColor" />
            </motion.div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">
                {currentSong.name}
              </div>
              <div className="text-xs text-white/60">Now Playing</div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
});

export default MobileVisualizer;
